import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Plus, 
  Edit, 
  Image as ImageIcon, 
  FileText, 
  MapPin, 
  Clock,
  Save,
  RefreshCw,
  Eye,
  Loader2,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AutoPoster = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [wikiData, setWikiData] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a monument name to search');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`/api/admin/fetch-wiki?query=${encodeURIComponent(searchQuery)}`);
      if (response.data.success) {
        setWikiData(response.data.data);
        setEditingData(response.data.data);
        setPreviewMode(true);
        toast.success('Data fetched successfully!');
      } else {
        toast.error(response.data.error || 'Failed to fetch Wikipedia data');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to fetch Wikipedia data');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!editingData) return;

    setSaving(true);
    try {
      const response = await axios.post('/api/admin/monuments', editingData);
      toast.success('Monument created successfully!');
      setWikiData(null);
      setEditingData(null);
      setPreviewMode(false);
      setSearchQuery('');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Failed to save monument');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingData || !editingData._id) return;

    setSaving(true);
    try {
      const response = await axios.put(`/api/admin/monuments/${editingData._id}`, editingData);
      toast.success('Monument updated successfully!');
      setWikiData(null);
      setEditingData(null);
      setPreviewMode(false);
      setSearchQuery('');
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update monument');
    } finally {
      setSaving(false);
    }
  };

  const handleChapterChange = (chapterIndex, field, value) => {
    setEditingData(prev => ({
      ...prev,
      chapters: prev.chapters.map((chapter, index) =>
        index === chapterIndex ? { ...chapter, [field]: value } : chapter
      )
    }));
  };

  const addChapter = () => {
    setEditingData(prev => ({
      ...prev,
      chapters: [...prev.chapters, {
        header: 'New Chapter',
        body: 'Chapter content goes here...',
        image: ''
      }]
    }));
  };

  const removeChapter = (index) => {
    setEditingData(prev => ({
      ...prev,
      chapters: prev.chapters.filter((_, i) => i !== index)
    }));
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-serif font-bold text-text-primary mb-6">
          Wikipedia Auto-Poster
        </h2>
        
        {/* Search Section */}
        <div className="glass-card p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search for any monument, historical site, or landmark..."
                className="w-full pl-10 pr-4 py-3 glass-morphism rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              {searching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              <span>Search Wikipedia</span>
            </button>
          </div>
          
          <div className="mt-4 text-sm text-text-secondary">
            <p>💡 Tip: Try searching for famous monuments like "Eiffel Tower", "Colosseum", or "Taj Mahal"</p>
          </div>
        </div>
      </div>

      {/* Preview/Edit Section */}
      {previewMode && editingData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="glass-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-serif font-semibold text-text-primary">
                {editingData._id ? 'Edit Monument' : 'Preview & Edit'}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPreviewMode(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={editingData._id ? handleUpdate : handleSave}
                  disabled={saving}
                  className="btn-primary flex items-center space-x-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{editingData._id ? 'Update' : 'Save'}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editingData.title}
                    onChange={(e) => setEditingData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={editingData.slug}
                    onChange={(e) => setEditingData(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Era
                  </label>
                  <input
                    type="text"
                    value={editingData.era}
                    onChange={(e) => setEditingData(prev => ({ ...prev, era: e.target.value }))}
                    className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Main Image URL
                  </label>
                  <input
                    type="text"
                    value={editingData.mainImage || ''}
                    onChange={(e) => setEditingData(prev => ({ ...prev, mainImage: e.target.value }))}
                    className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  />
                </div>

                {editingData.coordinates && (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Coordinates
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        value={editingData.coordinates[1]}
                        onChange={(e) => setEditingData(prev => ({
                          ...prev,
                          coordinates: [prev.coordinates[0], parseFloat(e.target.value)]
                        }))}
                        placeholder="Latitude"
                        className="px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                      />
                      <input
                        type="number"
                        value={editingData.coordinates[0]}
                        onChange={(e) => setEditingData(prev => ({
                          ...prev,
                          coordinates: [parseFloat(e.target.value), prev.coordinates[1]]
                        }))}
                        placeholder="Longitude"
                        className="px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div>
                <h4 className="text-lg font-medium text-text-primary mb-4">Preview</h4>
                <div className="glass-morphism rounded-lg overflow-hidden">
                  {editingData.mainImage && (
                    <img
                      src={editingData.mainImage}
                      alt={editingData.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <h5 className="text-lg font-serif font-semibold text-text-primary mb-2">
                      {editingData.title}
                    </h5>
                    <div className="flex items-center space-x-4 text-sm text-text-secondary mb-2">
                      <span className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{editingData.era}</span>
                      </span>
                      {editingData.coordinates && (
                        <span className="flex items-center space-x-1">
                          <MapPin className="w-4 h-4" />
                          <span>
                            {editingData.coordinates[1].toFixed(4)}°, {editingData.coordinates[0].toFixed(4)}°
                          </span>
                        </span>
                      )}
                    </div>
                    <p className="text-text-secondary text-sm">
                      {editingData.chapters[0]?.body?.substring(0, 150)}...
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chapters */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-text-primary">Chapters</h4>
                <button
                  onClick={addChapter}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Chapter</span>
                </button>
              </div>

              <div className="space-y-4">
                {editingData.chapters.map((chapter, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="glass-card p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-lg font-medium text-text-primary">
                        Chapter {index + 1}
                      </h5>
                      <button
                        onClick={() => removeChapter(index)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Header
                        </label>
                        <input
                          type="text"
                          value={chapter.header}
                          onChange={(e) => handleChapterChange(index, 'header', e.target.value)}
                          className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Image URL
                        </label>
                        <input
                          type="text"
                          value={chapter.image || ''}
                          onChange={(e) => handleChapterChange(index, 'image', e.target.value)}
                          className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Content
                      </label>
                      <textarea
                        value={chapter.body}
                        onChange={(e) => handleChapterChange(index, 'body', e.target.value)}
                        rows={6}
                        className="w-full px-4 py-2 glass-morphism rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50 resize-none"
                      />
                    </div>

                    {chapter.image && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Image Preview
                        </label>
                        <img
                          src={chapter.image}
                          alt={chapter.header}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AutoPoster;
